/**
 * @module  public/js/services/mirrornotesservice.js
 * @class   MirrorNotesService
 * @summary CRUD helper for CRM-less tenants storing notes in Firestore.
 *
 * @description
 * Provides a minimal wrapper around the `mirrorNotes` collection so the UI can
 * list and create notes without invoking Cloud Functions when a tenant is
 * operating in "local" CRM mode.  The service automatically scopes all reads
 * and writes to the current tenant and ensures author metadata is captured for
 * each note that is persisted.
 *
 * ## Public API
 * – `list(contactId)` – fetches notes for the contact ordered newest first
 * – `save({ contactId, text, noteId })` – creates or updates a mirrored note
 *
 * ## Dependencies
 * { AuthService, TenantResolver, crmUtils.getCurrentCRM }
 */

import {
  addDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';

import { colWithTenant, docWithTenant } from '../utils/firestoreHelpers.js';
import TenantResolver from './tenantResolver.js';

/**
 * Shape describing a mirrored note record.
 * @typedef {object} MirrorNote
 * @property {string} id
 * @property {string} contactId
 * @property {string} text
 * @property {string|null} authorUid
 * @property {string|null} createdBy
 * @property {string|null} createdAt
 * @property {string|null} updatedAt
 */

export default class MirrorNotesService {
  /** @type {import('../services/authservice.js').default} */
  #auth;
  /** @type {{ getCurrentCRM(userId: string): Promise<string> }} */
  #crmUtils;
  /** @type {TenantResolver} */
  #tenantResolver;
  /** @type {string|null} */
  #tenantId = null;

  /**
   * @param {object} deps
   * @param {import('../services/authservice.js').default} deps.auth
   * @param {{ getCurrentCRM(userId: string): Promise<string> }} deps.crmUtils
   * @param {{ tenantResolver?: TenantResolver }} [opts]
   */
  constructor({ auth, crmUtils }, opts = {}) {
    this.#auth = auth;
    this.#crmUtils = crmUtils;
    this.#tenantResolver = opts.tenantResolver || new TenantResolver(auth);
  }

  /**
   * Resolve and cache the active tenant identifier.
   * @returns {Promise<string>}
   * @throws {Error} when the tenant cannot be determined.
   */
  async #requireTenantId() {
    if (this.#tenantId) return this.#tenantId;
    const tenantId = await this.#tenantResolver.resolveTenantId();
    if (!tenantId) {
      throw new Error('MirrorNotesService: missing tenantId');
    }
    this.#tenantId = tenantId;
    return tenantId;
  }

  /**
   * Resolve the current Firebase user, waiting for login when necessary.
   * @returns {Promise<import('firebase/auth').User>}
   * @throws {Error} when no authenticated user is available.
   */
  async #requireUser() {
    let user = this.#auth.currentUser;
    if (!user?.uid) {
      user = await this.#auth.waitForLogin?.().catch(() => null);
    }
    if (!user?.uid) {
      throw new Error('MirrorNotesService: missing authenticated user');
    }
    return user;
  }

  /**
   * Ensure the current CRM context is `local` before touching Firestore.
   * @param {import('firebase/auth').User} user
   * @returns {Promise<void>}
   * @throws {Error} when the CRM is not in local mode.
   */
  async #assertLocalCrm(user) {
    const crm = await this.#crmUtils.getCurrentCRM(user.uid);
    if (crm !== 'local') {
      throw new Error('MirrorNotesService: CRM is not local');
    }
  }

  /**
   * Convert Firestore payloads into transport-friendly objects.
   * @param {string} id
   * @param {Record<string, any>} data
   * @returns {MirrorNote}
   */
  #normaliseNote(id, data = {}) {
    const createdAt = data.createdAt?.toDate?.() ?? data.createdAt ?? null;
    const updatedAt = data.updatedAt?.toDate?.() ?? data.updatedAt ?? null;
    const authorUid = data.authorUid ?? data.createdBy ?? null;
    return {
      id,
      contactId: data.contactId || '',
      text: data.text || '',
      authorUid,
      createdBy: data.createdBy ?? authorUid,
      createdAt: createdAt ? new Date(createdAt).toISOString() : null,
      updatedAt: updatedAt ? new Date(updatedAt).toISOString() : null,
    };
  }

  /**
   * List mirrored notes for a contact ordered newest first.
   * @param {string} contactId
   * @returns {Promise<MirrorNote[]>}
   */
  async list(contactId) {
    if (!contactId)
      throw new Error('MirrorNotesService.list: contactId required');
    const user = await this.#requireUser();
    await this.#assertLocalCrm(user);
    const tenantId = await this.#requireTenantId();
    const notesCol = colWithTenant(tenantId, 'mirrorNotes');
    const q = query(
      notesCol,
      where('contactId', '==', contactId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((docSnap) =>
      this.#normaliseNote(docSnap.id, docSnap.data())
    );
  }

  /**
   * Create or update a mirrored note for the contact.
   * @param {{ contactId: string, text: string, noteId?: string }} payload
   * @returns {Promise<MirrorNote>}
   */
  async save({ contactId, text, noteId = '' }) {
    const trimmed = String(text ?? '').trim();
    if (!trimmed) {
      throw new Error('MirrorNotesService.save: text required');
    }
    if (!contactId) {
      throw new Error('MirrorNotesService.save: contactId required');
    }
    const user = await this.#requireUser();
    await this.#assertLocalCrm(user);
    const tenantId = await this.#requireTenantId();
    const now = serverTimestamp();

    if (noteId) {
      const ref = docWithTenant(tenantId, `mirrorNotes/${noteId}`);
      await updateDoc(ref, {
        text: trimmed,
        updatedAt: now,
        authorUid: user.uid,
        createdBy: user.uid,
      });
      return {
        id: noteId,
        contactId,
        text: trimmed,
        authorUid: user.uid,
        createdBy: user.uid,
        createdAt: null,
        updatedAt: new Date().toISOString(),
      };
    }

    const colRef = colWithTenant(tenantId, 'mirrorNotes');
    const docRef = await addDoc(colRef, {
      contactId,
      text: trimmed,
      createdAt: now,
      updatedAt: now,
      authorUid: user.uid,
      createdBy: user.uid,
      createdByEmail: user.email ?? null,
      createdByName: user.displayName ?? null,
      tenantId,
    });
    return {
      id: docRef.id,
      contactId,
      text: trimmed,
      authorUid: user.uid,
      createdBy: user.uid,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }
}
